/**
 * Route Preservation Utility
 * Handles saving and restoring intended routes for unauthorized users
 */

const INTENDED_ROUTE_KEY = 'intendedRoute';

/**
 * Save the current route as the intended route
 * @param {string} pathname - The current pathname
 * @param {string} search - The current search parameters
 * @param {string} hash - The current hash
 */
export const saveIntendedRoute = (pathname, search = '', hash = '') => {
  // Don't save auth-related routes as intended routes
  const authRoutes = ['/login', '/signup'];
  
  if (authRoutes.includes(pathname)) {
    return;
  }

  const intendedRoute = {
    pathname,
    search,
    hash,
    timestamp: Date.now()
  };

  try {
    localStorage.setItem(INTENDED_ROUTE_KEY, JSON.stringify(intendedRoute));
  } catch (error) {
    console.warn('Failed to save intended route:', error);
  }
};

/**
 * Get the saved intended route
 * @returns {Object|null} The intended route object or null if not found/expired
 */
export const getIntendedRoute = () => {
  try {
    const saved = localStorage.getItem(INTENDED_ROUTE_KEY);
    if (!saved) return null;

    const intendedRoute = JSON.parse(saved);
    
    // Check if the saved route is not too old (24 hours)
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    if (Date.now() - intendedRoute.timestamp > maxAge) {
      clearIntendedRoute();
      return null;
    }

    return intendedRoute;
  } catch (error) {
    console.warn('Failed to get intended route:', error);
    clearIntendedRoute();
    return null;
  }
};

/**
 * Clear the saved intended route
 */
export const clearIntendedRoute = () => {
  try {
    localStorage.removeItem(INTENDED_ROUTE_KEY);
  } catch (error) {
    console.warn('Failed to clear intended route:', error);
  }
};

/**
 * Get the full URL path from an intended route object
 * @param {Object} intendedRoute - The intended route object
 * @returns {string} The full path including search and hash
 */
export const getFullPath = (intendedRoute) => {
  if (!intendedRoute) return null;
  
  return `${intendedRoute.pathname}${intendedRoute.search}${intendedRoute.hash}`;
};

/**
 * Get the intended route and clear it from storage
 * @returns {string|null} The full path of the intended route or null
 */
export const consumeIntendedRoute = () => {
  const intendedRoute = getIntendedRoute();
  if (intendedRoute) {
    clearIntendedRoute();
    return getFullPath(intendedRoute);
  }
  return null;
};

/**
 * Check if a route should be preserved (not an auth route)
 * @param {string} pathname - The pathname to check
 * @returns {boolean} True if the route should be preserved
 */
export const shouldPreserveRoute = (pathname) => {
  const authRoutes = ['/login', '/signup'];
  const worksuiteRoutes = ['/worksuite/listOrganization', '/worksuite/createOrganization', '/worksuite/editOrganization'];
  
  // Don't preserve auth routes or worksuite management routes
  return !authRoutes.includes(pathname) && 
         !worksuiteRoutes.some(route => pathname.startsWith(route));
};